"use client";

import {JSX} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoViewDeferred, type UseScrollIntoViewDeferredOptions} from "@/shared/hooks/dom/use-scroll-into-view-deferred";
import {useTranslation} from "@/shared/i18n";
import type {TranslationKey} from "@/shared/i18n/types";
import type {FormSubmitFeedbackModelInstance} from "@/shared/models/form/form-submit-feedback";

import "./styles/form-notify.scss";

const NOTIFY_SCROLL_INTO_VIEW: UseScrollIntoViewDeferredOptions = {
    behavior: "smooth",
    block: "end",
    inline: "nearest",
};

export type FormNotifyProps = {
    model: FormSubmitFeedbackModelInstance;
    className?: string;
};

function resolveKey(tRoot: (key: TranslationKey) => string, key: string | undefined): string {
    const k = key?.trim();
    return k ? tRoot(k as TranslationKey) : "";
}

export function FormNotify({model, className = ""}: FormNotifyProps): JSX.Element {
    const {t} = useTranslation();
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoViewDeferred<HTMLDivElement>(model, NOTIFY_SCROLL_INTO_VIEW);

    const b = snapshot.banner;

    let content: JSX.Element | null = null;
    if (b.type === "warning") {
        const titleText = resolveKey(t, b.title);
        const bodyText = t(b.message as TranslationKey);
        content = (
            <div className="form-notify__alert form-notify__alert--warning" role="alert">
                {titleText.length > 0 && <p className="form-notify__alert-title">{titleText}</p>}
                <p className="form-notify__alert-body">{bodyText}</p>
            </div>
        );
    } else if (b.type === "error_generic") {
        const titleText = resolveKey(t, b.title);
        const bodyKey = (b.message?.trim() ?? "forms.submitErrorGeneric") as TranslationKey;
        const bodyText = t(bodyKey);
        content = (
            <div className="form-notify__alert form-notify__alert--error" role="alert">
                {titleText.length > 0 && <p className="form-notify__alert-title">{titleText}</p>}
                <p className="form-notify__alert-body">{bodyText}</p>
            </div>
        );
    } else if (b.type === "error") {
        const titleText = resolveKey(t, b.title);
        const bodyText = t(b.message as TranslationKey);
        content = (
            <div className="form-notify__alert form-notify__alert--error" role="alert">
                {titleText.length > 0 && <p className="form-notify__alert-title">{titleText}</p>}
                <p className="form-notify__alert-body">{bodyText}</p>
            </div>
        );
    }

    const anchorClass = `form-notify__anchor${content == null ? " form-notify__anchor--empty" : ""} ${className}`.trim();

    return (
        <div ref={ref} className={anchorClass}>
            {content}
        </div>
    );
}
